import React from 'react';
import { Link } from 'expo-router';
import { Platform, StyleProp, StyleSheet, Text, View, ViewStyle } from 'react-native';
import colors from '../../lib/colors';
import { interRegular, interSemiBold } from '../../lib/fonts';
import { buildBillingPreviewTrialHref } from '../../lib/billingPreviewState';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

interface PreviewHiddenMatchesNoticeProps {
    totalFoundCount: number;
    shownCount: number;
    previewProjectId?: string | null;
    resultDigestId?: string | null;
    trialHref?: string | null;
    style?: StyleProp<ViewStyle>;
    testID?: string;
}

export default function PreviewHiddenMatchesNotice({
    totalFoundCount,
    shownCount,
    previewProjectId,
    resultDigestId,
    trialHref: authoritativeTrialHref,
    style,
    testID = 'preview-hidden-matches-card',
}: PreviewHiddenMatchesNoticeProps) {
    const { i18n } = useLingui();
    const hiddenCount = Math.max(0, totalFoundCount - shownCount);

    if (hiddenCount === 0) {
        return null;
    }

    const trialHref = authoritativeTrialHref || buildBillingPreviewTrialHref({
        projectId: previewProjectId,
        resultDigestId,
    });
    const hiddenLabel = i18n._({
        ...msg({
        id: 'preview.hiddenMatchesCount',
        message: '{count, plural, one {# more paper found} other {# more papers found}}',
        }),
        values: { count: hiddenCount },
    });

    return (
        <View style={[styles.card, style]} testID={testID}>
            <View style={styles.header}>
                <Text
                    style={[styles.title, interSemiBold]}
                    accessibilityRole="header"
                    {...(Platform.OS === 'web' && { role: 'heading', 'aria-level': 3 })}
                >
                    {hiddenLabel}
                </Text>
            </View>
            <Text style={[styles.body, interRegular]}>{hiddenCount === 1 ? <Trans id="preview.hiddenMatches.unlockOne" comment="Trial CTA for one hidden preview paper">
                <Link href={trialHref} style={[styles.link, interSemiBold]}>Start your 14-day free trial</Link>
                {' '}to unlock it and resume tracking.
            </Trans> : <Trans id="preview.hiddenMatches.unlockMany" comment="Trial CTA for multiple hidden preview papers">
                <Link href={trialHref} style={[styles.link, interSemiBold]}>Start your 14-day free trial</Link>
                {' '}to unlock them and resume tracking.
            </Trans>}
            </Text>
        </View>
    );
}

const styles = StyleSheet.create({
    card: {
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: '#cfe2bf',
        borderRadius: 14,
        backgroundColor: '#f8fbf5',
    },
    header: {
        paddingHorizontal: 16,
        paddingVertical: 10,
        backgroundColor: '#e9f3e2',
        borderBottomWidth: 1,
        borderBottomColor: '#d8e8cc',
    },
    title: {
        color: colors.primaryGreenDark,
        fontSize: 16,
        lineHeight: 22,
    },
    body: {
        paddingHorizontal: 16,
        paddingVertical: 12,
        color: colors.text600,
        fontSize: 13,
        lineHeight: 20,
    },
    link: {
        color: colors.primaryGreenDark,
        textDecorationLine: 'underline',
    },
});
